import { useState, useEffect } from 'react';
import { Package, Plus, Pencil, Trash2, Loader2, GripVertical, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { orgApi } from '../../utils/apiV2';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import ServiceToggle from '../ServiceToggle';

const EMPTY = { name: '', description: '', discountPercent: 10, serviceIds: [], active: true, allowSkip: true };

export const PackagesSection = ({ services = [] }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const activeServices = services.filter((s) => s.active !== false);

  const load = async () => {
    try {
      const res = await orgApi.getPackages();
      setPackages(res.data?.packages || []);
    } catch (e) {
      console.error('load packages failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowDialog(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description || '', discountPercent: p.discountPercent || 0, serviceIds: [...(p.serviceIds || [])], active: p.active, allowSkip: p.allowSkip !== false });
    setShowDialog(true);
  };

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      serviceIds: f.serviceIds.includes(id) ? f.serviceIds.filter((s) => s !== id) : [...f.serviceIds, id],
    }));
  };

  const save = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) { toast.error('Give the package a name'); return; }
    if (form.serviceIds.length < 2) { toast.error('Pick at least 2 services to bundle'); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        discountPercent: Number(form.discountPercent) || 0,
        serviceIds: form.serviceIds,
        active: form.active,
        allowSkip: form.allowSkip !== false,
      };
      if (editing) await orgApi.updatePackage(editing.id, body);
      else await orgApi.createPackage(body);
      toast.success(editing ? 'Package updated' : 'Package created — it\'s live on your booking page');
      setShowDialog(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the package');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await orgApi.updatePackage(p.id, { active: !p.active });
      setPackages((list) => list.map((x) => (x.id === p.id ? { ...x, active: !p.active } : x)));
    } catch (e) {
      toast.error('Could not update the package');
    }
  };

  const remove = async (p) => {
    setDeleting(p.id);
    try {
      await orgApi.deletePackage(p.id);
      toast.success('Package deleted');
      setPackages((list) => list.filter((x) => x.id !== p.id));
    } catch (e) {
      toast.error('Could not delete the package');
    } finally {
      setDeleting(null);
    }
  };

  const svcName = (id) => services.find((s) => s.id === id)?.name || 'Unknown service';

  const pkgIssues = (p) => {
    const issues = [];
    (p.services || []).forEach((s) => {
      if (s.active === false) issues.push(`${s.name} is turned off`);
      else if (s.durationModel === 'subscription') issues.push(`${s.name} became a subscription`);
    });
    if ((p.serviceIds || []).length > (p.services || []).length) issues.push('a bundled service was deleted');
    return issues;
  };
  const unhealthy = packages.filter((p) => p.active && pkgIssues(p).length > 0);

  return (
    <div className="mt-10" data-testid="packages-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" /> Packages
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">Bundle 2+ services into one guided booking with one payment — set a package discount and it goes live on your booking page.</p>
        </div>
        <Button onClick={openCreate} data-testid="new-package-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-1.5" /> New package
        </Button>
      </div>

      {unhealthy.length > 0 && (
        <div className="mb-4 bg-amber-50 rounded-2xl p-4 flex items-start gap-3" data-testid="packages-health-alert">
          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-800">
              {unhealthy.length === 1 ? '1 package is' : `${unhealthy.length} packages are`} hidden from your booking page
            </p>
            {unhealthy.map((p) => (
              <p key={p.id} className="text-xs text-amber-700 mt-1" data-testid={`package-health-row-${p.id}`}>
                <span className="font-semibold">{p.name}</span> — {pkgIssues(p).join(', ')}. Turn the service back on or edit the package to bring it back.
              </p>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : packages.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl p-8 text-center" data-testid="packages-empty-state">
          <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No packages yet — bundle your services and offer a "book together" discount.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {packages.map((p) => {
            const issues = pkgIssues(p);
            return (
            <div key={p.id} className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(2,12,27,0.06)] p-4" data-testid={`package-row-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 text-sm truncate">{p.name}</div>
                  <div className="text-xs text-gray-500 mt-1 leading-relaxed">
                    {(p.serviceIds || []).map(svcName).join(' + ')}
                  </div>
                  {p.active && issues.length > 0 && (
                    <div className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-[11px] font-medium" data-testid={`package-hidden-badge-${p.id}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      Hidden — {issues.join(', ')}
                    </div>
                  )}
                </div>
                <ServiceToggle checked={p.active} onChange={() => toggleActive(p)} data-testid={`package-active-toggle-${p.id}`} />
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs font-bold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1">
                  {Math.round(p.discountPercent || 0)}% off bundle
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" data-testid={`package-edit-btn-${p.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(p)} disabled={deleting === p.id} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" data-testid={`package-delete-btn-${p.id}`}>
                    {deleting === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="package-builder-dialog">
          <DialogTitle>{editing ? 'Edit package' : 'New package'}</DialogTitle>
          <DialogDescription className="text-sm text-gray-500 -mt-1">
            Customers book every experience in one flow and pay once. Discount applies to each service's price.
          </DialogDescription>
          <div className="space-y-4 mt-1">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Package name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Dubai Day Out" maxLength={255} data-testid="package-name-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Description <span className="text-gray-400 font-normal">(shown on the card)</span></label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="A full day of adventure — jet ski, then parasail over JBR." rows={2} maxLength={2000} data-testid="package-description-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Services in this package <span className="text-gray-400 font-normal">(tap in booking order)</span></label>
              <div className="max-h-52 overflow-y-auto rounded-xl bg-gray-50 p-2 space-y-1" data-testid="package-service-picker">
                {activeServices.map((s) => {
                  const order = form.serviceIds.indexOf(s.id);
                  return (
                    <button key={s.id} onClick={() => toggleService(s.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors ${order >= 0 ? 'bg-blue-50' : 'bg-white hover:bg-gray-100'}`}
                      data-testid={`package-service-option-${s.id}`}>
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${order >= 0 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'}`}>
                        {order >= 0 ? order + 1 : <GripVertical className="w-3 h-3" />}
                      </span>
                      <span className="text-sm text-gray-800 truncate flex-1">{s.name}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">{s.currency || ''} {s.basePrice || s.price || 0}</span>
                    </button>
                  );
                })}
                {activeServices.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No active services yet</p>}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Package discount %</label>
                <Input type="number" min={0} max={75} value={form.discountPercent}
                  onChange={(e) => setForm({ ...form, discountPercent: e.target.value })}
                  data-testid="package-discount-input" />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <ServiceToggle checked={form.active} onChange={(v) => setForm({ ...form, active: v })} data-testid="package-form-active-toggle" />
                <span className="text-sm text-gray-600">Live</span>
              </div>
            </div>
            <div className="flex items-start justify-between gap-3 bg-gray-50 border border-gray-200 rounded-xl p-3">
              <div>
                <p className="text-sm font-semibold text-gray-800">Allow skipping unavailable experiences</p>
                <p className="text-xs text-gray-500 mt-0.5">If a service is sold out or has no availability for the customer's dates, they can skip it and still book the rest of the package.</p>
              </div>
              <div className="pt-0.5">
                <ServiceToggle checked={form.allowSkip !== false} onChange={(v) => setForm({ ...form, allowSkip: v })} data-testid="package-allow-skip-toggle" />
              </div>
            </div>
            <Button onClick={save} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="package-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Save changes' : 'Create package')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesSection;
